import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { cx } from '../utils/cx.js';
import styles from './Tooltip.module.css';

export type TooltipProps = {
  /** Plain string or structured content (use `wide` for trees, lists). */
  label: ReactNode;
  /** Wider, left-aligned panel with scroll — hover stays open while pointer over tip. */
  wide?: boolean;
  /**
   * Preferred side. Portal tips flip when preferred side lacks space in the viewport.
   * `top` (default): tip above trigger. `bottom`: below.
   */
  position?: 'top' | 'bottom';
  /** Stretch trigger to full table cell width (ellipsis uses column space, not shrink-wrapped content). */
  fill?: boolean;
  /** Render tip in `document.body` (escapes `overflow: hidden` in tables, file pickers, etc.). */
  portal?: boolean;
  children: ReactNode;
};

type TipCoords = { top: number; left: number; maxHeight: number };
type TipSide = 'top' | 'bottom';

const HOVER_CLOSE_DELAY_MS = 160;
const VIEW_MARGIN = 8;
const GAP = 6;
const MIN_TIP_HEIGHT = 96;

function tipClassName(wide: boolean | undefined, side: TipSide, portal: boolean) {
  return cx(
    styles.tip,
    wide && styles.tipWide,
    side === 'bottom' && styles.tipBottom,
    portal && styles.tipPortal,
  );
}

/** Place portal tip inside the viewport; flip preferred side when space is short. */
function placePortalTip(
  preferred: TipSide,
  anchor: DOMRect,
  tipW: number,
  tipH: number,
): { side: TipSide; top: number; left: number; maxHeight: number } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const spaceBelow = Math.max(0, vh - anchor.bottom - GAP - VIEW_MARGIN);
  const spaceAbove = Math.max(0, anchor.top - GAP - VIEW_MARGIN);

  let side = preferred;
  if (preferred === 'bottom') {
    if (tipH > spaceBelow && spaceAbove > spaceBelow) side = 'top';
  } else if (tipH > spaceAbove && spaceBelow > spaceAbove) {
    side = 'bottom';
  }

  // Cap height to the larger free band so tall content scrolls instead of clipping.
  const maxHeight = Math.max(MIN_TIP_HEIGHT, side === 'bottom' ? spaceBelow : spaceAbove);

  const measuredH = tipH > 0 ? Math.min(tipH, maxHeight) : Math.min(120, maxHeight);
  const measuredW = tipW > 0 ? tipW : 200;

  let top =
    side === 'bottom' ? anchor.bottom + GAP : anchor.top - GAP - measuredH;
  const maxTop = Math.max(VIEW_MARGIN, vh - measuredH - VIEW_MARGIN);
  top = Math.min(Math.max(VIEW_MARGIN, top), maxTop);

  // Recompute remaining viewport under final top (guards clamp edge cases).
  const maxHeightFinal = Math.max(MIN_TIP_HEIGHT, vh - top - VIEW_MARGIN);

  const halfW = measuredW / 2;
  let left = anchor.left + anchor.width / 2;
  left = Math.min(Math.max(halfW + VIEW_MARGIN, left), vw - halfW - VIEW_MARGIN);

  return { side, top, left, maxHeight: Math.min(maxHeight, maxHeightFinal) };
}

export function Tooltip({
  label,
  children,
  wide,
  position = 'top',
  fill,
  portal = false,
}: TooltipProps) {
  const tipId = useId();
  const anchorRef = useRef<HTMLSpanElement>(null);
  const tipRef = useRef<HTMLSpanElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<TipCoords | null>(null);
  const [side, setSide] = useState<TipSide>(position);
  const hoverBridge = wide || portal;

  const cancelScheduledHide = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const hideNow = useCallback(() => {
    cancelScheduledHide();
    setOpen(false);
    if (portal) setCoords(null);
    setSide(position);
  }, [cancelScheduledHide, portal, position]);

  const scheduleHide = useCallback(() => {
    if (!hoverBridge) {
      hideNow();
      return;
    }
    cancelScheduledHide();
    closeTimerRef.current = setTimeout(() => {
      setOpen(false);
      if (portal) setCoords(null);
      setSide(position);
      closeTimerRef.current = null;
    }, HOVER_CLOSE_DELAY_MS);
  }, [cancelScheduledHide, hideNow, hoverBridge, portal, position]);

  useEffect(() => () => cancelScheduledHide(), [cancelScheduledHide]);

  const updateCoords = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const r = anchor.getBoundingClientRect();
    const tipEl = tipRef.current;
    const placed = placePortalTip(
      position,
      r,
      tipEl?.offsetWidth ?? 0,
      tipEl?.scrollHeight ?? tipEl?.offsetHeight ?? 0,
    );
    setSide(placed.side);
    setCoords({ top: placed.top, left: placed.left, maxHeight: placed.maxHeight });
  }, [position]);

  const show = useCallback(() => {
    cancelScheduledHide();
    setOpen(true);
    setSide(position);
    if (!portal) return;
    const anchor = anchorRef.current;
    if (!anchor) return;
    const r = anchor.getBoundingClientRect();
    const placed = placePortalTip(position, r, 0, 0);
    setSide(placed.side);
    setCoords({ top: placed.top, left: placed.left, maxHeight: placed.maxHeight });
  }, [cancelScheduledHide, portal, position]);

  useLayoutEffect(() => {
    if (!portal || !open) return;
    updateCoords();
    const tipEl = tipRef.current;
    const ro =
      tipEl && typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => updateCoords())
        : null;
    if (tipEl && ro) ro.observe(tipEl);
    const onReflow = () => updateCoords();
    window.addEventListener('scroll', onReflow, true);
    window.addEventListener('resize', onReflow);
    return () => {
      ro?.disconnect();
      window.removeEventListener('scroll', onReflow, true);
      window.removeEventListener('resize', onReflow);
    };
  }, [portal, open, updateCoords, label]);

  const resolvedSide = portal ? side : position;

  const tipStyle: CSSProperties | undefined =
    portal && coords
      ? {
          position: 'fixed',
          top: coords.top,
          left: coords.left,
          transform: 'translateX(-50%)',
          maxHeight: coords.maxHeight,
          overflowY: 'auto',
        }
      : undefined;

  const tipNode = (
    <span
      ref={portal ? tipRef : undefined}
      id={tipId}
      className={tipClassName(wide, resolvedSide, portal)}
      style={tipStyle}
      role="tooltip"
      data-wp-mist-scroll={wide || portal ? '' : undefined}
      onMouseEnter={hoverBridge ? cancelScheduledHide : undefined}
      onMouseLeave={hoverBridge ? scheduleHide : undefined}
    >
      {label}
    </span>
  );

  return (
    <span
      ref={anchorRef}
      className={cx(styles.wrap, fill && styles.wrapFill)}
      tabIndex={-1}
      aria-describedby={open ? tipId : undefined}
      onMouseEnter={show}
      onMouseLeave={scheduleHide}
      onFocus={show}
      onBlur={scheduleHide}
    >
      {children}
      {portal ? (open ? createPortal(tipNode, document.body) : null) : tipNode}
    </span>
  );
}
